import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Workspace } from '@pinnakl/crm/domain';
import { withDevtools } from '@pinnakl/shared/signal-store';
import { promiseWithTimeout } from '@pinnakl/shared/util-helpers';
import { CrmWorkspacesApiService } from './crm-workspaces-api.service';

interface CrmWorkspacesState {
  workspaces: Workspace[];
  selectedWorkspaceId: number | null;
  workspacesLoading: boolean;
  workspacesLoaded: boolean;
  // True from the moment the first fetch starts. Marks the end of the boot window in which core-API
  // requests (/v4/users above all) legitimately go out unscoped, so crmWorkspaceHeaderInterceptor
  // can tell "not scoped yet" apart from "scoping failed" and only fail closed on the latter.
  workspacesLoadAttempted: boolean;
}

const featureKey = 'crmWorkspaces';
const STORAGE_KEY = 'crmSelectedWorkspaceId';
// How long anything may block on the workspace load before continuing unresolved. Both the guard
// and the header interceptor gate on it, so an unbounded wait here holds the entire app on the
// spinner rather than surfacing an error (WI 8623).
export const WORKSPACE_RESOLUTION_TIMEOUT_MS = 20_000;

function parseWorkspaceId(raw: string | null): number | null {
  const parsed = raw === null ? NaN : Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function readStoredWorkspaceId(): number | null {
  const fromSession = parseWorkspaceId(sessionStorage.getItem(STORAGE_KEY));
  return fromSession ?? parseWorkspaceId(localStorage.getItem(STORAGE_KEY));
}

function storeWorkspaceId(workspaceId: number | null): void {
  if (workspaceId === null) {
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
  } else {
    const value = String(workspaceId);
    sessionStorage.setItem(STORAGE_KEY, value);
    localStorage.setItem(STORAGE_KEY, value);
  }
}

export const CrmWorkspacesStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withDevtools(featureKey),
  withState<CrmWorkspacesState>({
    workspaces: [],
    selectedWorkspaceId: readStoredWorkspaceId(),
    workspacesLoading: false,
    workspacesLoaded: false,
    workspacesLoadAttempted: false,
  }),
  withComputed(store => ({
    hasMultipleWorkspaces: computed(() => store.workspacesLoaded() && store.workspaces().length > 1),
    selectedWorkspace: computed(
      () => store.workspaces().find(workspace => workspace.id === store.selectedWorkspaceId()) ?? null,
    ),
  })),
  withComputed(store => ({
    isLpWorkspaceSelected: computed(() => store.selectedWorkspace()?.code === 'LP'),
    isGpWorkspaceSelected: computed(() => store.selectedWorkspace()?.code === 'GP'),
    isInvestmentsWorkspaceSelected: computed(() => store.selectedWorkspace()?.code === 'INVESTMENTS'),
    activeWorkspaceId: computed(() => store.selectedWorkspace()?.id ?? null),
  })),
  withMethods((store, api = inject(CrmWorkspacesApiService)) => {
    let inFlight: Promise<Workspace[]> | null = null;

    const fetchWorkspaces = async (): Promise<Workspace[]> => {
      patchState(store, { workspacesLoading: true, workspacesLoadAttempted: true });
      try {
        const workspaces = await api.load();
        const selectedWorkspaceId = workspaces.some(w => w.id === store.selectedWorkspaceId())
          ? store.selectedWorkspaceId()
          : ((workspaces.find(w => w.isDefault) ?? workspaces[0])?.id ?? null);
        patchState(store, { workspaces, selectedWorkspaceId, workspacesLoading: false, workspacesLoaded: true });
        storeWorkspaceId(selectedWorkspaceId);
        return workspaces;
      } catch (e) {
        console.error(e);
        // Leave workspaces empty and workspacesLoaded false: activeWorkspaceId then stays null, which
        // is what makes the guard and the interceptor fail closed instead of falling back to the BE
        // default workspace. Keeping loaded false also lets the next call retry rather than cache it.
        patchState(store, { workspacesLoading: false });
        return [];
      }
    };

    // Only clear the latch if it is still ours, so a forceFetch started while another load is in
    // flight is not dropped by the older one settling.
    const runFetch = (): Promise<Workspace[]> => {
      const promise = fetchWorkspaces().finally(() => {
        if (inFlight === promise) inFlight = null;
      });
      inFlight = promise;
      return promise;
    };

    return {
      selectWorkspace: (workspaceId: number | null): void => {
        patchState(store, { selectedWorkspaceId: workspaceId });
        storeWorkspaceId(workspaceId);
      },
      reset: (): void => {
        inFlight = null;
        patchState(store, {
          workspaces: [],
          workspacesLoading: false,
          workspacesLoaded: false,
          workspacesLoadAttempted: false,
        });
      },
      loadWorkspaces: (forceFetch = false): Promise<Workspace[]> => {
        if (forceFetch) {
          return runFetch();
        }
        if (store.workspacesLoaded()) {
          return Promise.resolve(store.workspaces());
        }
        return inFlight ?? runFetch();
      },
      // null means "no workspace may be sent", never "send the default one" — see the interceptor,
      // which distinguishes the boot window from a failed load before deciding what to do with it.
      whenWorkspaceResolved: async (): Promise<number | null> => {
        // Every workspace-scoped request queues behind this one await, so it must never park
        // indefinitely. On expiry fall through with whatever is resolved: null makes the
        // interceptor fail closed, which is a visible error instead of a hang (WI 8623).
        if (inFlight) {
          await promiseWithTimeout(inFlight, WORKSPACE_RESOLUTION_TIMEOUT_MS, () => {
            console.error('[workspace backstop] whenWorkspaceResolved timed out - requests will fail closed');
            return [];
          });
        }
        return store.activeWorkspaceId();
      },
    };
  }),
);
